const PBKDF2_ITERATIONS = 210_000;
const SALT_BYTES = 16;
const KEY_BITS = 256;

const toHex = (bytes: Uint8Array) =>
  Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

const fromHex = (hex: string) => {
  const bytes = new Uint8Array(hex.length / 2);
  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = parseInt(hex.slice(index * 2, index * 2 + 2), 16);
  }
  return bytes;
};

const deriveKey = async (password: string, salt: Uint8Array) => {
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveBits'],
  );

  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    KEY_BITS,
  );

  return new Uint8Array(bits);
};

export const createPasswordRecord = async (password: string) => {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const hash = await deriveKey(password, salt);
  return { salt: toHex(salt), hash: toHex(hash) };
};

export const verifyPassword = async (
  password: string,
  saltHex: string,
  hashHex: string,
) => {
  if (!saltHex || !hashHex || hashHex.length % 2 !== 0) return false;

  const derived = await deriveKey(password, fromHex(saltHex));
  const expected = fromHex(hashHex);
  if (derived.length !== expected.length) return false;

  let different = false;
  for (let index = 0; index < derived.length; index += 1) {
    if (derived[index] !== expected[index]) {
      different = true;
    }
  }
  return !different;
};
